import React, { FC, memo, useLayoutEffect } from 'react'

import { useSetFocus } from '@please/lrud'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'

import { updateOffers } from '@/actions'
import { Button } from '@/components/Button/Button'
import { ContentUnavailable } from '@/components/ContentUnavailable/ContentUnavailable'
import { Loader } from '@/components/Loader'
import { PageFallback } from '@/components/PageFallback/PageFallback'
import { SettingsHeader } from '@/components/SettingsHeader'
import { focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { paths } from '@/constants/paths'
import { subscriptionsLayoutData } from '@/constants/settings'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'

import {
  ButtonDescription,
  PageContentUnavailableWrapper,
  LoginButtonWrapper,
  SubscriptionsWrapper,
  SettingsSubscriptionsWrapper,
} from './styles'
import { SubscriptionsPanel } from './SubscbriptionPanel'

const focusId = `${focusIds.SETTINGS_SUBSCRIPTIONS}${focusIds.CONTAINER_MOD}`

const SubscriptionsRaw: FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useCommonTranslation()
  const setFocus = useSetFocus()

  const { offers, setOffers, error, loading } = useStore(
    (state) => state.promotion,
  )
  const filteredOffers =
    offers && offers.filter((offer) => offer.showToUser ?? true)

  const { isAuthenticatedUserType, isRegisteredUser } = useAuth()

  const headerDescription = offers?.length
    ? t(subscriptionsLayoutData.description)
    : ''

  const isSettingsRoute = pathname?.includes(paths.SETTINGS)

  useLayoutEffect(() => {
    if (!isAuthenticatedUserType) {
      return
    }

    void updateOffers()

    if (window.location.search.includes('?withFocus') || !isSettingsRoute) {
      setTimeout(() => {
        setFocus(focusId)
      }, 0)
    }
    return () => {
      setOffers(null)
    }
  }, [isAuthenticatedUserType])

  const goToLogin = () => navigate(paths.LOGIN)

  const SubscriptionsComponent = isSettingsRoute
    ? SettingsSubscriptionsWrapper
    : SubscriptionsWrapper

  return (
    <SubscriptionsComponent
      focusId={focusId}
      elementType="div"
      orientation={orientation.VERTICAL}
      className={isSettingsRoute ? '' : 'content-wrapper'}
    >
      {loading && <Loader />}
      {error && (
        <PageContentUnavailableWrapper>
          <ContentUnavailable>
            <PageFallback reloadHandler={updateOffers} />
          </ContentUnavailable>
        </PageContentUnavailableWrapper>
      )}
      {!loading && !error && (
        <>
          <SettingsHeader
            title={t(subscriptionsLayoutData.title)}
            description={headerDescription}
          />
          <SubscriptionsPanel offers={filteredOffers} />
          {!isRegisteredUser && offers !== null && (
            <LoginButtonWrapper>
              <ButtonDescription>{t('haveAccount')}</ButtonDescription>
              <Button
                iconSrc={icons.register.loginIcon}
                text={t('login')}
                onClick={goToLogin}
              />
            </LoginButtonWrapper>
          )}
        </>
      )}
    </SubscriptionsComponent>
  )
}

export const Subscriptions = memo(SubscriptionsRaw)
